import { deepComparison } from "client/Misc/compareUtils";

export type PopStateHandler = (e: PopStateEvent) => void;

// NOTE: cannot be used to compare ref equal when retrieved from history
const NULL_MARKER = { ["__NULL__"]: true } as const;

function attachToPopState(handler: PopStateHandler): PopStateHandler {
	// There are some browser (e.g. iOS Safari) which fire the "popstate" event
	// on page load while this is not according to the spec. To overcome this, we
	// make sure the state can never be null by putting an empty object in state when it
	// is (null or undefined).

	const initialState: any = getRawState();
	if (!initialState) {
		// We make sure the current page (on load) has state
		// url can't be undefined as it will case IE (11) to replace the url with /undefined
		replaceState(NULL_MARKER, "", null);
	}

	window.addEventListener("popstate", handlePopState);

	function handlePopState(args: PopStateEvent): void {
		// Webkit fires a popstate event on page load. The state will be null, so we are able to detect it
		// but only reliable when we don't push/replace the state with null values

		const stateFromHistory: any = getRawState();

		const currentPageHasState: boolean = stateFromHistory !== null && typeof stateFromHistory !== "undefined";

		// args.state can only be null when it is fired on page load. Else the
		// state should atleast be an empty object. which makes sure the handler is called.
		const isPageLoadPopState: boolean = (args.state === null) && currentPageHasState;

		if (isPageLoadPopState) {
			return;
		}

		handler(args);
	}

	// return the subscribed function so it could be possible to unsubscribe later
	return handlePopState;
}

function detachFromPopState(handler: PopStateHandler): void {
	window.removeEventListener("popstate", handler);
}

function getRawState(): any {
	return window.history.state;
}

function getState(): any {
	const state: any = getRawState();

	const isNullMarker: boolean = deepComparison(state, NULL_MARKER);
	if (isNullMarker) {
		return null;
	}
	return state;
}

function pushState(state: any, title: string, url: string): void {
	if (state === null) {
		state = NULL_MARKER;
	}

	window.history.pushState(state, title, url);
}

function replaceState(state: any, title: string, url: string | null): void {
	if (state === null) {
		state = NULL_MARKER;
	}

	window.history.replaceState(state, title, url);
}

export {
	attachToPopState,
	detachFromPopState,
	pushState,
	replaceState,
	getState
};